import React, {useEffect, useState} from 'react';
import {Select, Spin} from 'antd';
import {Status} from '../../types';
import {useTranslation} from '../../hooks/useTranslation';
import {useStatus} from "./hooks/useStatus";
import {getSeverityColor} from "../../utils/ColorHelper";

interface SelectStatusProps {
  value?: string | string[];
  placeholder?: string;
  onChange?: (value: string, option: Status) => void;
  type: string; // Type to status options (e.g., 'RO', 'EV', 'RI', etc.)
  disabled?: boolean;
  allowClear?: boolean;
  optionKey?: string;
  isFilterable?: boolean;
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
  className?: string;
  mode?: 'multiple' | 'tags';
  id?: string;
  variant?: "outlined" | "borderless" | "filled" | "underlined" | undefined;
  initialOptions?: Status[];
}

const SelectStatus: React.FC<SelectStatusProps> = ({
                                                     value,
                                                     placeholder,
                                                     onChange,
                                                     type,
                                                     disabled = false,
                                                     allowClear = true,
                                                     size = 'middle',
                                                     style,
                                                     className,
                                                     mode,
                                                     id,
                                                     variant,
                                                     initialOptions = []
                                                   }) => {
  const {t} = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [mergedOptions, setMergedOptions] = useState<Status[]>(initialOptions);

  const {data: options = [], isLoading, error} = useStatus(
    {
      valueFind: type
    },
    {
      enabled: isDropdownOpen
    }
  );

  useEffect(() => {
    if (options.length > 0) {
      const loadedOptionIds = new Set(options.map(opt => opt.id));

      const uniqueInitialOptions = initialOptions.filter(
        initOpt => !loadedOptionIds.has(initOpt.id)
      );

      setMergedOptions([...uniqueInitialOptions, ...options]);
    } else if (initialOptions.length > 0) {
      setMergedOptions(initialOptions);
    }
  }, [options, initialOptions]);

  const handleDropdownVisibleChange = (open: boolean) => {
    setIsDropdownOpen(open);
  };

  const handleChange = (selectedValue: string | string[] | undefined) => {
    if (selectedValue === undefined || selectedValue === null || selectedValue === '') {
      // Handle clear action
      if (onChange) {
        onChange('', {} as Status);
      }
      return;
    }

    const selectedStatus = mergedOptions.find(status => status.id === selectedValue);
    if (onChange && selectedStatus) {
      onChange(selectedValue as string, selectedStatus);
    }
  };

  const handleSearch = (searchText: string) => {
    setSearchValue(searchText);
  };

  const filterOption = (input: string, option?: any) => {
    if (!input) return true;
    const searchText = input.toLowerCase();
    const optionData = mergedOptions.find(opt => opt.id === option?.value);

    if (!optionData) return false;

    return (
      (optionData.description?.toLowerCase().includes(searchText) || false) ||
      (optionData.abbreviation?.toLowerCase().includes(searchText) || false)
    );
  };

  if (error) {
    console.error('Error loading options:', error);
  }

  return (
    <Select
      id={id}
      value={value}
      placeholder={placeholder || t('messages.select')}
      onChange={handleChange}
      onOpenChange={handleDropdownVisibleChange}
      disabled={disabled}
      allowClear={allowClear}
      variant={variant}
      size={size}
      style={style}
      className={className}
      mode={mode}
      showSearch={{optionFilterProp: "children", onSearch: handleSearch, filterOption: filterOption}}
      loading={isLoading}
      notFoundContent={isLoading ? <Spin size="small"/> : t('messages.notFound')}
    >
      {mergedOptions.map((option) => (
        <Select.Option
          key={option.id || option.description}
          value={option.id || option.description}
          title={option.description}
        >
          <div style={{display: 'flex', alignItems: 'center'}}>
            {option.colorStateIssue && (
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: getSeverityColor(option.colorStateIssue),
                  marginRight: 8,
                  border: '1px',
                  borderStyle: 'solid',
                  borderColor: '#' + option.colorStateIssue
                }}
              />
            )}
            <span>{option.description}</span>
          </div>
        </Select.Option>
      ))}
    </Select>
  );
};

export default SelectStatus;
